import { useFragment } from "@/generated";
import {
  ConfirmLessonGenerationDocument,
  type ConfirmLessonGenerationInput,
  DiscardLessonGenerationDocument,
  LessonGenerationJobDocument,
  LessonGenerationJobFieldsFragmentDoc,
  LessonGenerationJobsByProjectDocument,
  LessonGenerationJobsDocument,
  LessonSlideAudioDocument,
  RegenerateLessonSectionDocument,
  type RegenerateLessonSectionInput,
  SaveLessonGenerationDraftDocument,
  StartLessonGenerationBatchDocument,
  type StartLessonGenerationBatchInput,
  StartLessonGenerationDocument,
  type StartLessonGenerationInput,
} from "@/generated/graphql";
import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";

const POLL_INTERVAL_MS = 1500;

interface JobActivity {
  status: string;
  stage?: string | null;
}

/** A stage outlives `done`: confirming a draft leaves the job writing, then filling in slide audio. */
const isJobActive = (job: JobActivity) =>
  job.status === "pending" || job.status === "running" || job.stage != null;

function usePollWhileActive(
  jobs: JobActivity[],
  startPolling: (interval: number) => void,
  stopPolling: () => void
) {
  const isAnyActive = jobs.some(isJobActive);

  useEffect(() => {
    if (!isAnyActive) {
      stopPolling();
      return;
    }
    startPolling(POLL_INTERVAL_MS);
    return stopPolling;
  }, [isAnyActive, startPolling, stopPolling]);
}

export function useStartLessonGeneration() {
  const [mutate, { loading }] = useMutation(StartLessonGenerationDocument);

  const start = async (input: StartLessonGenerationInput) => {
    const { data } = await mutate({ variables: { input } });
    const started = data?.startLessonGeneration;
    return started ? useFragment(LessonGenerationJobFieldsFragmentDoc, started).id : null;
  };

  return { start, starting: loading };
}

export function useConfirmLessonGeneration() {
  const [mutate, { loading }] = useMutation(ConfirmLessonGenerationDocument);

  const confirm = async (input: ConfirmLessonGenerationInput): Promise<string[]> => {
    const { data } = await mutate({ variables: { input } });
    const job = data?.confirmLessonGeneration;
    return job ? useFragment(LessonGenerationJobFieldsFragmentDoc, job).writtenSlideIds : [];
  };

  return { confirm, confirming: loading };
}

export function useLessonGenerationJobs(lessonId: string) {
  const { data, loading, refetch, startPolling, stopPolling } = useQuery(
    LessonGenerationJobsDocument,
    { variables: { lessonId }, fetchPolicy: "cache-and-network" }
  );

  const [discardJob] = useMutation(DiscardLessonGenerationDocument);

  const jobs = (data?.lessonGenerationJobs ?? []).map((job) =>
    useFragment(LessonGenerationJobFieldsFragmentDoc, job)
  );

  usePollWhileActive(jobs, startPolling, stopPolling);

  return {
    jobs,
    loading,
    refetch,
    discard: async (jobId: string) => {
      await discardJob({ variables: { jobId } });
      await refetch();
    },
  };
}

export function useSaveLessonGenerationDraft() {
  const [mutate] = useMutation(SaveLessonGenerationDraftDocument);
  return {
    saveDraft: async (input: ConfirmLessonGenerationInput) => {
      await mutate({ variables: { input } });
    },
  };
}

export function useRegenerateLessonSection() {
  const [mutate, { loading }] = useMutation(RegenerateLessonSectionDocument);
  return {
    regenerateSection: async (input: RegenerateLessonSectionInput) => {
      await mutate({ variables: { input } });
    },
    regenerating: loading,
  };
}

export function useLessonGenerationJob(jobId: string | null) {
  const { data, error, startPolling, stopPolling } = useQuery(LessonGenerationJobDocument, {
    variables: { id: jobId ?? "" },
    skip: jobId === null,
    fetchPolicy: "network-only",
  });

  const masked = data?.lessonGenerationJob;
  const job = masked ? useFragment(LessonGenerationJobFieldsFragmentDoc, masked) : null;
  const isFinished = (job?.status === "done" || job?.status === "failed") && !job?.stage;

  useEffect(() => {
    if (jobId === null || isFinished) {
      stopPolling();
      return;
    }
    startPolling(POLL_INTERVAL_MS);
    return stopPolling;
  }, [jobId, isFinished, startPolling, stopPolling]);

  return { job, error };
}

export function useGenerationRun(resumeJobId: string | null = null) {
  const [jobId, setJobId] = useState<string | null>(resumeJobId);
  const { start, starting } = useStartLessonGeneration();
  const { job, error } = useLessonGenerationJob(jobId);

  const isRunning = starting || job?.status === "pending" || job?.status === "running";

  return {
    jobId,
    job,
    error,
    isRunning,
    start: async (input: StartLessonGenerationInput) => setJobId(await start(input)),
    reset: () => setJobId(null),
  };
}

/**
 * The slides on this lesson whose audio is still generating, so their cards can say so. Polls only
 * while a job is actually in the audio stage, and calls `onFinished` once it ends — the slides were
 * fetched before their audio existed, so they need re-reading to show it.
 */
export function useGeneratingSlideAudio(lessonId: string, onFinished?: () => void) {
  const { data, startPolling, stopPolling } = useQuery(LessonSlideAudioDocument, {
    variables: { lessonId },
    fetchPolicy: "cache-and-network",
  });

  const jobs = data?.lessonGenerationJobs ?? [];
  const isGenerating = jobs.some((job) => job.stage === "audio");
  const wasGenerating = useRef(false);

  useEffect(() => {
    if (!isGenerating) {
      stopPolling();
      return;
    }
    startPolling(POLL_INTERVAL_MS);
    return stopPolling;
  }, [isGenerating, startPolling, stopPolling]);

  useEffect(() => {
    if (wasGenerating.current && !isGenerating) onFinished?.();
    wasGenerating.current = isGenerating;
  }, [isGenerating, onFinished]);

  return new Set(
    jobs.flatMap((job) =>
      job.audioSlides.filter((slide) => slide.status === "pending").map((slide) => slide.slideId)
    )
  );
}

export function useStartLessonGenerationBatch() {
  const [mutate, { loading }] = useMutation(StartLessonGenerationBatchDocument);

  const startBatch = async (input: StartLessonGenerationBatchInput) => {
    const { data } = await mutate({ variables: { input } });
    return (data?.startLessonGenerationBatch ?? []).map((job) =>
      useFragment(LessonGenerationJobFieldsFragmentDoc, job)
    );
  };

  return { startBatch, starting: loading };
}

export function useProjectGenerationJobs(projectId: string) {
  const { data, loading, refetch, startPolling, stopPolling } = useQuery(
    LessonGenerationJobsByProjectDocument,
    { variables: { projectId }, fetchPolicy: "cache-and-network" }
  );

  const [discardJob] = useMutation(DiscardLessonGenerationDocument);

  const jobs = (data?.lessonGenerationJobsByProject ?? []).map((job) =>
    useFragment(LessonGenerationJobFieldsFragmentDoc, job)
  );

  usePollWhileActive(jobs, startPolling, stopPolling);

  return {
    jobs,
    loading,
    refetch,
    discard: async (jobId: string) => {
      await discardJob({ variables: { jobId } });
      await refetch();
    },
  };
}
